// src/components/dashboard/DashboardThreadDetail.tsx
// FIXED — merge artifacts from applying the counterpartyLabel patch:
//   1. counterpartyLabel state was declared inside CommitEngagementForm
//      (where it's never used) instead of DashboardThreadDetail (where
//      it's actually read in the message render loop and set from the
//      fetch response). Moved to the correct component.
//   2. The "(drafted by AI)" span was duplicated — rendered twice per
//      assistant-drafted message. Reduced to one.
//
// MIGRATED — same as DashboardTimeline.tsx: local FDW_BASE /
// dashboardToken / getDashboardToken / authedFetch block removed in
// favor of the shared dashboardApi client. Four call sites updated:
// /dashboard/timeline/messages, /dashboard/profile/engagement-requirements,
// /dashboard/timeline/threads (×2), /dashboard/timeline/commit,
// /dashboard/timeline/reply — all now relative paths through the /api
// prefix baked into the shared client.
//
// UPDATED (this pass) — engagementRef is now sourced from the thread
// object returned by /timeline/threads (get-message-threads.ts's own
// engagementRef field), not from parseThreadId(threadId). Prior to
// commit-engagement.ts's thread-linkage fix (this session), threadId's
// embedded engagementRef segment was the ONLY value ever available —
// and it reflects whatever a counterparty happened to pass into
// send_message pre-commitment (send-message.ts's deriveThreadId), which
// is not guaranteed to match commit_engagement's own server-generated
// engagementRef. Now that commit-engagement.ts writes its real
// engagementRef back onto the thread doc, the already-fetched
// threadStatus effect is the correct, reliable source — no new fetch
// needed, just capturing a field that was already coming back and being
// discarded. parseThreadId's engagementRef output is no longer used
// anywhere in this file; counterpartyId remains its only real use,
// since that value is stable and doesn't have the same drift risk.
//
// NEW — RecordFindingForm, the manual-attestation counterpart to
// CommitEngagementForm. Calls record_transaction_finding via the
// generic /dashboard/tools/:toolName dispatcher (same path
// get_outcome_completion_status already uses) — no new dashboard.ts
// route needed. Rendered once an engagement is committed or verified;
// requires a real engagementRef, which is why it's gated on
// engagementRef being non-null rather than just on threadStatus.
//
// NEW — completion-status badge, fetched via get_outcome_completion_status
// once engagementRef is known. Re-fetches after a finding is recorded
// so the badge updates without a full page reload.
import React, { useState, useEffect } from 'react';
import { useParams, Link, useOutletContext } from 'react-router-dom';
import { authedFetch, authedFetchRaw } from '../../services/dashboardApi';
import { ArrowLeft, Loader2Icon } from 'lucide-react';
import { UserProfile } from '../../types';
import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport,
} from '../ui/message-scroller';

interface MessageRecord {
  messageId: string;
  senderUid: string;
  body: string;
  timestamp: string;
  draftedBy: 'human' | 'assistant';
}

interface EngagementRequirements {
  applicable: boolean;
  minimumProjectValueCents: number | null;
  requiresWrittenBrief: boolean;
  discoveryCallRequired: boolean;
  minimumTimelineDays: number | null;
}

interface CompletionStatus {
  allResolved: boolean;
  completionCriteriaMet: boolean | null;
  reportedAt: string | null;
}

// threadId is constructed as [entityId, counterpartyId, engagementRef|'unscoped'].join(':')
// per get-message-threads.ts's own derivation — parsed back out here
// for counterpartyId ONLY. Do NOT use this for engagementRef (see file
// header) — the thread doc's own engagementRef field, fetched via
// /timeline/threads, is the reliable source now that commit-engagement.ts
// writes it back at commit time.
function parseThreadId(threadId: string): { counterpartyId: string } {
  const parts = threadId.split(':');
  const counterpartyId = parts[1] ?? '';
  return { counterpartyId };
}

function CommitEngagementForm({
  counterpartyId,
  requirements,
  onCommitted,
}: {
  counterpartyId: string;
  requirements: EngagementRequirements | null;
  onCommitted: () => void;
}) {
  const [agreedValue, setAgreedValue] = useState('');
  const [scope, setScope] = useState('');
  const [discoveryCallCompleted, setDiscoveryCallCompleted] = useState(false);
  const [briefReceived, setBriefReceived] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const handleCommit = async () => {
    const cents = Math.round(parseFloat(agreedValue) * 100);
    if (!cents || cents <= 0 || scope.trim().length < 10) {
      setError('Enter a valid agreed value and a scope summary of at least 10 characters.');
      return;
    }
    setIsCommitting(true);
    setError(null);
    try {
      const res = await authedFetch('/timeline/commit', {
        method: 'POST',
        body: JSON.stringify({
          counterpartyId,
          agreedValueCents: cents,
          scope: scope.trim(),
          discoveryCallCompleted,
          briefReceived,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to commit engagement.');
      if (data.committed === false) {
        setError(data.message);
        return;
      }
      onCommitted();
    } catch (err: any) {
      setError(err.message || 'Could not commit this engagement.');
    } finally {
      setIsCommitting(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="rounded-lg bg-brand-accent px-4 py-2 text-sm font-medium text-white hover:bg-brand-accentHover"
      >
        Commit this engagement
      </button>
    );
  }

  return (
    <div className="space-y-3 rounded-xl border border-brand-border bg-brand-white p-4">
      <h3 className="text-sm font-medium text-brand-black">Commit engagement</h3>
      {requirements?.applicable && requirements.minimumProjectValueCents != null && (
        <p className="text-xs text-brand-gray">
          Your minimum is ${(requirements.minimumProjectValueCents / 100).toLocaleString()} — values below this will be rejected.
        </p>
      )}
      <input
        value={agreedValue}
        onChange={(e) => setAgreedValue(e.target.value)}
        placeholder="Agreed value ($)"
        className="w-full rounded-lg border border-brand-border px-3 py-2 text-sm"
      />
      <textarea
        value={scope}
        onChange={(e) => setScope(e.target.value)}
        placeholder="Scope of work, timeline, or terms agreed"
        rows={3}
        className="w-full resize-none rounded-lg border border-brand-border px-3 py-2 text-sm"
      />
      {requirements?.discoveryCallRequired && (
        <label className="flex items-center gap-2 text-sm text-brand-charcoal">
          <input type="checkbox" checked={discoveryCallCompleted} onChange={(e) => setDiscoveryCallCompleted(e.target.checked)} />
          Discovery call completed
        </label>
      )}
      {requirements?.requiresWrittenBrief && (
        <label className="flex items-center gap-2 text-sm text-brand-charcoal">
          <input type="checkbox" checked={briefReceived} onChange={(e) => setBriefReceived(e.target.checked)} />
          Written brief received
        </label>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex justify-end gap-2">
        <button onClick={() => setOpen(false)} className="rounded-lg px-3 py-1.5 text-sm text-brand-charcoal hover:bg-brand-surface">
          Cancel
        </button>
        <button
          onClick={handleCommit}
          disabled={isCommitting}
          className="rounded-lg bg-brand-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-accentHover disabled:opacity-50"
        >
          {isCommitting ? <Loader2Icon className="inline size-4 animate-spin" /> : 'Confirm commitment'}
        </button>
      </div>
    </div>
  );
}

function RecordFindingForm({
  engagementRef,
  onRecorded,
}: {
  engagementRef: string;
  onRecorded: () => void;
}) {
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<'info' | 'review_required' | 'escalated'>('info');
  const [resolutionNote, setResolutionNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const handleSubmit = async () => {
    if (description.trim().length < 10) {
      setError('Description must be at least 10 characters.');
      return;
    }
    if (resolutionNote.trim().length < 10) {
      setError('Resolution note must be a genuine, specific account — at least 10 characters.');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const res = await authedFetchRaw('/dashboard/tools/record_transaction_finding', {
        method: 'POST',
        body: JSON.stringify({
          engagementRef,
          description: description.trim(),
          severity,
          resolutionNote: resolutionNote.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to record finding.');
      if (data.recorded === false) {
        setError(data.message);
        return;
      }
      setDescription('');
      setResolutionNote('');
      setOpen(false);
      onRecorded();
    } catch (err: any) {
      setError(err.message || 'Could not record this finding.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="rounded-lg border border-brand-border px-4 py-2 text-sm font-medium text-brand-charcoal hover:bg-brand-surface"
      >
        Record a reviewed finding
      </button>
    );
  }

  return (
    <div className="space-y-3 rounded-xl border border-brand-border bg-brand-white p-4">
      <h3 className="text-sm font-medium text-brand-black">Record a reviewed finding</h3>
      <p className="text-xs text-brand-gray">
        This is the actual evidence FDW checks before independently verifying this engagement as
        complete — write a genuine account, not a placeholder.
      </p>

      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="What did you review? e.g. Reviewed the vendor's redlined NDA against our standard terms."
        rows={2}
        className="w-full resize-none rounded-lg border border-brand-border px-3 py-2 text-sm"
      />

      <select
        value={severity}
        onChange={(e) => setSeverity(e.target.value as typeof severity)}
        className="w-full rounded-lg border border-brand-border px-3 py-2 text-sm bg-brand-white"
      >
        <option value="info">Info — routine, no issues</option>
        <option value="review_required">Review required — needed a decision</option>
        <option value="escalated">Escalated — needed to go beyond normal handling</option>
      </select>

      <textarea
        value={resolutionNote}
        onChange={(e) => setResolutionNote(e.target.value)}
        placeholder="How was this resolved? Be specific — this note is what makes verification possible."
        rows={3}
        className="w-full resize-none rounded-lg border border-brand-border px-3 py-2 text-sm"
      />

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex justify-end gap-2">
        <button onClick={() => setOpen(false)} className="rounded-lg px-3 py-1.5 text-sm text-brand-charcoal hover:bg-brand-surface">
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="rounded-lg bg-brand-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-accentHover disabled:opacity-50"
        >
          {isSubmitting ? <Loader2Icon className="inline size-4 animate-spin" /> : 'Record finding'}
        </button>
      </div>
    </div>
  );
}

export const DashboardThreadDetail: React.FC = () => {
  const { threadId } = useParams<{ threadId: string }>();
  const { user } = useOutletContext<{ user: UserProfile }>();
  const [messages, setMessages] = useState<MessageRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [requirements, setRequirements] = useState<EngagementRequirements | null>(null);
  const [committed, setCommitted] = useState(false);
  const [counterpartyLabel, setCounterpartyLabel] = useState<string | null>(null);
  // Real committed state, sourced from the thread list's own status
  // join (get-message-threads.ts) rather than inferred from the URL's
  // threadId — engagementRef in the URL only reflects what was true
  // when this page was navigated to, not what's true now. null = not
  // yet checked.
  const [threadStatus, setThreadStatus] = useState<'new' | 'negotiating' | 'committed' | 'verified' | 'settled' | null>(null);
  // NEW — the thread doc's own engagementRef, captured from the same
  // /timeline/threads fetch that resolves threadStatus. This is the
  // reliable source (see file header) — do not derive this from
  // parseThreadId.
  const [engagementRef, setEngagementRef] = useState<string | null>(null);
  // NEW — completion status for the engagement, once known.
  const [completionStatus, setCompletionStatus] = useState<CompletionStatus | null>(null);

  const { counterpartyId } = threadId ? parseThreadId(threadId) : { counterpartyId: '' };

  useEffect(() => {
    if (!threadId) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await authedFetch('/timeline/messages', {
          method: 'POST',
          body: JSON.stringify({ threadId }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Failed to load this thread.');
        }
        const data = await res.json();
        if (!cancelled) setMessages(data.messages);
        if (!cancelled) setCounterpartyLabel(data.counterpartyLabel ?? null);
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Could not load this thread.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [threadId]);

  // Engagement requirements — reuses the already-real, already-verified
  // profile/engagement-requirements route rather than adding a new
  // one, since the data is entity-wide, not thread-specific.
  useEffect(() => {
    (async () => {
      try {
        const res = await authedFetch('/profile/engagement-requirements');
        if (res.ok) setRequirements(await res.json());
      } catch {
        // Non-fatal — banner just won't show if this fails.
      }
    })();
  }, []);

  // UPDATED — now also captures engagementRef from the matched thread
  // object, not just status. Same fetch, one more field read from it.
  useEffect(() => {
    if (!threadId) return;
    (async () => {
      try {
        const res = await authedFetch('/timeline/threads', { method: 'POST' });
        if (!res.ok) return;
        const data = await res.json();
        const match = data.threads?.find((t: { threadId: string }) => t.threadId === threadId);
        if (match) {
          setThreadStatus(match.status);
          setEngagementRef(match.engagementRef ?? null);
        }
      } catch {
        // Non-fatal — commit form just falls back gracefully below.
      }
    })();
  }, [threadId]);

  // NEW — fetches completion status once engagementRef is known.
  // Re-runs when engagementRef changes (e.g. right after commit) and
  // can be re-triggered manually via the refetch key bump in
  // RecordFindingForm's onRecorded callback.
  const [completionRefetchKey, setCompletionRefetchKey] = useState(0);
  useEffect(() => {
    if (!engagementRef) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetchRaw('/dashboard/tools/get_outcome_completion_status', {
          method: 'POST',
          body: JSON.stringify({ engagementRef }),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data.found) {
          setCompletionStatus(data.state);
        }
      } catch {
        // Non-fatal — badge just won't show.
      }
    })();
    return () => { cancelled = true; };
  }, [engagementRef, completionRefetchKey]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || !threadId || isSending) return;

    setIsSending(true);
    setSendError(null);

    try {
      const res = await authedFetch('/timeline/reply', {
        method: 'POST',
        body: JSON.stringify({ message: text, threadId, draftedBy: 'human' }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to send message.');
      }
      const result = await res.json();
      setMessages((prev) => [
        ...prev,
        {
          messageId: result.messageId,
          senderUid: user.uid,
          body: text,
          timestamp: result.timestamp,
          draftedBy: 'human',
        },
      ]);
      setDraft('');
    } catch (err: any) {
      setSendError(err.message || 'Could not send this message.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col space-y-4">
      <Link
        to="/dashboard/timeline"
        className="inline-flex items-center gap-1.5 text-sm text-brand-charcoal hover:text-brand-black shrink-0"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Back to Timeline
      </Link>

      {requirements?.applicable && requirements.minimumProjectValueCents != null && (
        <div className="shrink-0 rounded-lg bg-brand-surface px-3 py-2 text-xs text-brand-charcoal">
          Your minimum project value is <span className="font-medium">${(requirements.minimumProjectValueCents / 100).toLocaleString()}</span>
          {requirements.discoveryCallRequired && ' · Discovery call required'}
          {requirements.requiresWrittenBrief && ' · Written brief required'}
        </div>
      )}

      {isLoading && <p className="text-sm text-brand-gray font-mono">Loading messages…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {!isLoading && !error && messages.length === 0 && (
        <p className="text-sm text-brand-gray">No messages in this thread yet.</p>
      )}

      {!isLoading && !error && messages.length > 0 && (
        <MessageScrollerProvider defaultScrollPosition="last-anchor">
          <MessageScroller className="flex-1 rounded-xl border border-brand-border bg-brand-white">
            <MessageScrollerViewport>
              <MessageScrollerContent className="p-4">
                {messages.map((m) => {
                  const isEntitySide = m.senderUid === user.uid;
                  return (
                    <MessageScrollerItem
                      key={m.messageId}
                      messageId={m.messageId}
                      scrollAnchor={isEntitySide}
                    >
                      <div
                        className={`rounded-xl border p-4 ${
                          isEntitySide
                            ? 'border-brand-accent/30 bg-brand-surface ml-8'
                            : 'border-brand-border bg-brand-white mr-8'
                        }`}
                      >
                        <div className="flex justify-between items-baseline mb-1.5">
                          <span className="text-xs font-mono text-brand-gray">
                            {isEntitySide ? 'You' : (counterpartyLabel ?? m.senderUid)}
                            {m.draftedBy === 'assistant' && (
                              <span className="ml-1.5 text-brand-gray/70">(drafted by AI)</span>
                            )}
                          </span>
                          <span className="text-xs text-brand-gray">
                            {new Date(m.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-brand-black whitespace-pre-wrap">{m.body}</p>
                      </div>
                    </MessageScrollerItem>
                  );
                })}
              </MessageScrollerContent>
            </MessageScrollerViewport>
            <MessageScrollerButton />
          </MessageScroller>
        </MessageScrollerProvider>
      )}

      {!isLoading && !error && counterpartyId && !committed &&
        (threadStatus === 'new' || threadStatus === 'negotiating') && (
        <div className="shrink-0">
          <CommitEngagementForm
            counterpartyId={counterpartyId}
            requirements={requirements}
            onCommitted={() => setCommitted(true)}
          />
        </div>
      )}

      {(committed || threadStatus === 'committed' || threadStatus === 'verified' || threadStatus === 'settled') && (
        <div className="shrink-0 space-y-2">
          <p className="text-sm text-green-700">
            {threadStatus === 'verified' ? 'Engagement verified.' : 'Engagement committed.'}
          </p>

          {completionStatus && (
            <p className="text-xs text-brand-gray">
              {completionStatus.reportedAt
                ? `Verified complete · ${new Date(completionStatus.reportedAt).toLocaleDateString()}`
                : completionStatus.allResolved
                  ? 'All items resolved — verification pending'
                  : 'Awaiting fulfillment tracking'}
            </p>
          )}

          {engagementRef && threadStatus === 'committed' && (
            <RecordFindingForm
              engagementRef={engagementRef}
              onRecorded={() => setCompletionRefetchKey((k) => k + 1)}
            />
          )}
        </div>
      )}

      {!isLoading && !error && (
        <div className="shrink-0 space-y-2">
          {sendError && <p className="text-sm text-red-600">{sendError}</p>}
          <div className="flex items-end gap-2 rounded-xl border border-brand-border bg-brand-white p-3">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Reply to this thread..."
              rows={1}
              disabled={isSending}
              className="max-h-32 min-h-9 flex-1 resize-none border-none bg-transparent text-sm text-brand-black outline-none placeholder:text-brand-gray disabled:opacity-50"
            />
            <button
              onClick={handleSend}
              disabled={isSending || !draft.trim()}
              className="shrink-0 rounded-lg bg-brand-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-accentHover disabled:opacity-50"
            >
              {isSending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};